import { Box, ButtonBase, InputBase, styled } from '@mui/material';
import { Check } from 'lucide-react';
import { getDropdownPaperSx } from 'src/utils/dropdown-paper';

type SelectMenuTriggerVariant = 'outlined' | 'inline';

export const SelectMenuTriggerButton = styled(ButtonBase, {
  name: 'SelectMenuTrigger',
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'variant',
})<{ open?: boolean; variant?: SelectMenuTriggerVariant }>(({ theme, open, variant }) => {
  const isInline = variant === 'inline';
  const outlinedBackgroundColor =
    theme.palette.mode === 'light'
      ? theme.palette.custom.OV.sys.surfaceContainerLow
      : theme.palette.custom.OV.sys.surfaceContainerLowest;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    // `min()` keeps the floor from forcing overflow inside narrower containers/viewports.
    minWidth: isInline ? 0 : 'min(9.75rem, 100%)',
    maxWidth: '100%',
    boxSizing: 'border-box',
    height: isInline ? 'auto' : '2rem',
    padding: isInline ? theme.spacing(0.5, 1) : '0 0.5625rem 0 0.6875rem',
    borderRadius: '0.5rem',
    border: isInline ? 'none' : `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
    backgroundColor: isInline ? 'transparent' : outlinedBackgroundColor,
    color: theme.palette.custom.OV.sys.onBackground,
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.78125rem',
    fontWeight: isInline ? theme.typography.body2.fontWeight : 600,
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
    ...(open &&
      (isInline
        ? { backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh }
        : { borderColor: theme.palette.custom.OV.sys.outline })),
    '&:hover': {
      ...(isInline
        ? { backgroundColor: theme.palette.custom.OV.sys.surface }
        : { borderColor: theme.palette.custom.OV.sys.outline }),
    },
    '&:focus-visible': {
      ...(isInline
        ? { outline: `1px solid ${theme.palette.custom.OV.sys.primary}` }
        : { borderColor: theme.palette.custom.OV.sys.primary }),
    },
    '&:hover [data-trigger-clear], &:focus-visible [data-trigger-clear]': {
      opacity: 1,
    },
    '&.Mui-disabled': {
      opacity: 0.5,
    },
  };
});

// Vertical geometry note: line boxes and glyph sizes are pinned so that every
// flex-centered child leaves an EVEN remainder inside its container, i.e. all
// vertical offsets land on whole CSS pixels. Fractional offsets (e.g. a
// ~18.75px "normal" line box in a 22px slot) snap on 1x displays and make
// otherwise-centered rows look off by a pixel.
export const TriggerIconSlot = styled('span', { name: 'SelectMenuTriggerIcon' })(
  ({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    '& svg': { width: '1.3125rem', height: '1.3125rem' },
  }),
);

export const TriggerLabel = styled('span', { name: 'SelectMenuTriggerLabel' })({
  flexGrow: 1,
  minWidth: 0,
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: '1.125rem',
});

// Free-form replacement for TriggerLabel (chips, avatars): grows like the
// label but lets its content wrap instead of truncating.
export const TriggerContent = styled('span', { name: 'SelectMenuTriggerContent' })({
  display: 'flex',
  flexGrow: 1,
  minWidth: 0,
  textAlign: 'left',
});

export const TriggerChevron = styled('span', { name: 'SelectMenuTriggerChevron' })(
  ({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    '& svg': { width: '1rem', height: '1rem' },
  }),
);

// Lives inside the trigger <button>, so it is a span with a click handler
// rather than a nested button; keyboard users clear with Backspace/Delete on
// the trigger itself. Hidden until the trigger is hovered or focused (see
// SelectMenuTriggerButton), always visible on touch where hover never fires.
export const TriggerClearButton = styled('span', { name: 'SelectMenuTriggerClear' })(
  ({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '1rem',
    height: '1rem',
    borderRadius: '50%',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    opacity: 0,
    transition: 'opacity 120ms ease, color 120ms ease',
    '&:hover': { color: theme.palette.custom.OV.sys.onBackground },
    '& svg': { width: '1rem', height: '1rem' },
    '@media (hover: none)': { opacity: 1 },
  }),
);

// Same colour as a checked option so the two "this is the current value"
// signals read as one system.
export const TriggerSpinner = styled('span', { name: 'SelectMenuTriggerSpinner' })(
  ({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    color: theme.palette.custom.OV.sys.primary,
  }),
);

export const CommandRoot = styled(Box, { name: 'SelectMenuCommand' })(({ theme }) => ({
  // The list must be the only scroll container: the chain from the Popover
  // paper down to [cmdk-list] is a non-scrolling flex column that lets the
  // list shrink (minHeight: 0) and scroll on short viewports.
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  '& [cmdk-root]': {
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  '& [cmdk-list]': {
    flex: '1 1 auto',
    minHeight: 0,
    maxHeight: '18.75rem',
    overflowY: 'auto',
    outline: 'none',
    padding: '0.375rem',
  },
  '& [cmdk-item]': {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.625rem',
    padding: '0.5rem 0.5625rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    // Clears the sticky group heading, which would otherwise cover a scrolled-to row.
    scrollMarginTop: '1.75rem',
  },
  '& [cmdk-item][data-selected="true"]': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? theme.palette.custom.OV.sys.surfaceContainer
        : theme.palette.custom.OV.sys.surfaceContainerHighest,
  },
  '& [cmdk-item][data-disabled="true"]': {
    opacity: 0.5,
    cursor: 'default',
  },
  // Headings pin while their own section scrolls past, matching the MUI
  // Autocomplete groupLabel they replace. The negative `top` cancels the list's
  // padding so rows can't peek above a pinned heading.
  '& [cmdk-group-heading]': {
    position: 'sticky',
    top: '-0.375rem',
    zIndex: 1,
    padding: '0.5rem 0.5625rem 0.125rem',
    backgroundColor: getDropdownPaperSx(theme).backgroundColor,
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: '1rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
  },
  // Checkboxes keep their slot (no text shift) but only show when checked or
  // on the hovered/highlighted row; group rows only when checked.
  '& [cmdk-item] [data-checkbox]:not([data-checked])': { visibility: 'hidden' },
  '& [cmdk-item][data-selected="true"]:not([data-command]) [data-checkbox]': {
    visibility: 'visible',
  },
}));

export const SearchRow = styled(Box, { name: 'SelectMenuSearchRow' })(({ theme }) => ({
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: '0.5rem 0.9375rem',
  borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  '& > svg': { width: '0.875rem', height: '0.875rem', flexShrink: 0 },
}));

export const SearchInput = styled(InputBase, { name: 'SelectMenuSearchInput' })(
  ({ theme }) => ({
    flexGrow: 1,
    fontSize: '0.8125rem',
    color: theme.palette.custom.OV.sys.onBackground,
    // Pin the input to a 16px box (InputBase defaults to uneven 4px/5px padding)
    // so the search row height and its centered offsets stay integers.
    '& input': {
      padding: 0,
      height: '1rem',
      lineHeight: '1rem',
    },
    '& input::placeholder': {
      color: theme.palette.custom.OV.sys.onSurfaceVariant,
      opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      '& input': {
        height: '1.25rem',
        lineHeight: '1.25rem',
      },
    },
  }),
);

export const HotkeyGlyph = styled('kbd', { name: 'SelectMenuHotkey' })(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  minWidth: '1rem',
  height: '1rem',
  padding: '0 0.1875rem',
  borderRadius: '0.25rem',
  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.6875rem',
  lineHeight: '1rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  // Hotkey glyphs are meaningless without a keyboard; hide them on touch
  // devices (same query as useScreenDimensions' isTouchDevice). CSS-only —
  // the keyboard behavior itself stays active everywhere.
  '@media (hover: none) and (pointer: coarse)': {
    display: 'none',
  },
}));

export const OptionText = styled('span', { name: 'SelectMenuOptionText' })({
  flex: '1 1 0%',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
});

export const OptionTitleRow = styled('span', { name: 'SelectMenuOptionTitleRow' })(
  ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    fontSize: '0.78125rem',
    fontWeight: 600,
    lineHeight: '1.125rem',
    color: theme.palette.custom.OV.sys.onBackground,
  }),
);

export const OptionTitleLabel = styled('span', { name: 'SelectMenuOptionTitleLabel' })({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const OptionCheck = styled('span', { name: 'SelectMenuOptionCheck' })(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  marginLeft: 'auto',
  // Guaranteed clearance from the (truncating) title so text never touches it.
  paddingLeft: '0.75rem',
  color: theme.palette.custom.OV.sys.primary,
  '& svg': { width: '1rem', height: '1rem' },
}));

// Decorative check for multi-select rows: the row owns the click and
// aria-checked. Hidden/hover-reveal rules live in CommandRoot and FlyoutOption,
// keyed on the data-checkbox/data-checked attributes.
//
// No box — a bare check, matching OptionCheck. Because unchecked checks only
// render on the hovered/highlighted row, the dimmed state IS the hover state:
// a ghost of the check that clicking will commit.
const OptionCheckboxGlyph = styled('span', { name: 'SelectMenuOptionCheckbox' })(
  ({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    marginLeft: 'auto',
    // Guaranteed clearance from the (truncating) title so text never touches it.
    paddingLeft: '0.75rem',
    // Match the 1.125rem title line box so centered offsets stay integers.
    height: '1.125rem',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    opacity: 0.3,
    '&[data-checked]': { color: theme.palette.custom.OV.sys.primary, opacity: 1 },
    '& svg': { width: '1rem', height: '1rem' },
  }),
);

export const OptionCheckbox = ({ checked }: { checked: boolean }) => (
  <OptionCheckboxGlyph aria-hidden data-checkbox="" data-checked={checked || undefined}>
    <Check />
  </OptionCheckboxGlyph>
);

export const MenuStatus = styled('div', { name: 'SelectMenuStatus' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.8125rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
}));

// Rows inside the group flyout; mirrors [cmdk-item] styling.
export const FlyoutOption = styled(ButtonBase, { name: 'SelectMenuFlyoutOption' })(
  ({ theme }) => ({
    width: '100%',
    justifyContent: 'flex-start',
    // The native <button> UA style centers text; flex alignment doesn't undo it.
    textAlign: 'left',
    gap: '0.625rem',
    padding: '0.5rem 0.5625rem',
    borderRadius: '0.5rem',
    fontFamily: theme.typography.fontFamily,
    '&:hover, &[data-active="true"]': {
      backgroundColor:
        theme.palette.mode === 'light'
          ? theme.palette.custom.OV.sys.surfaceContainer
          : theme.palette.custom.OV.sys.surfaceContainerHighest,
    },
    '& [data-checkbox]:not([data-checked])': { visibility: 'hidden' },
    '&:hover [data-checkbox]:not([data-checked]), &[data-active="true"] [data-checkbox]:not([data-checked])':
      { visibility: 'visible' },
    '&.Mui-disabled': { opacity: 0.5 },
  }),
);

// Dimmed inline text: `Group ›` breadcrumb on search rows, selected-child hint on group rows.
export const OptionHint = styled('span', { name: 'SelectMenuOptionHint' })(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.125rem',
  flexShrink: 0,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontWeight: 400,
  fontSize: '0.78125rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  '& svg': { width: '0.75rem', height: '0.75rem', flexShrink: 0 },
}));

export const CreateActionDivider = styled(Box, { name: 'SelectMenuCreateActionDivider' })(
  ({ theme }) => ({
    flexShrink: 0,
    borderTop: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  }),
);

export const CreateActionRow = styled(ButtonBase, { name: 'SelectMenuCreateActionRow' })(
  ({ theme }) => ({
    flexShrink: 0,
    width: '100%',
    justifyContent: 'flex-start',
    textAlign: 'left',
    gap: '0.4375rem',
    padding: '0.5625rem',
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.78125rem',
    fontWeight: 600,
    color: theme.palette.custom.OV.sys.secondary,
    '& svg': { width: '0.8125rem', height: '0.8125rem' },
    '&:hover, &[data-active="true"]': {
      backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
    },
  }),
);

export const OptionDescription = styled('span', { name: 'SelectMenuOptionDescription' })(
  ({ theme }) => ({
    marginTop: '0.125rem',
    fontSize: '0.71875rem',
    lineHeight: '1rem',
    // Unbroken long words must wrap instead of widening the row.
    overflowWrap: 'anywhere',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
  }),
);
